import React, { useEffect, useMemo } from "react";
import {
  Background,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  type Edge,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import type { DependencyGraph } from "../../../models";
import { predTypeLabel, formatLagSuffix } from "../../../utils/p6";
import ActivityNode, { type ActivityNodeData } from "./ActivityNode";
import { layoutGraph } from "./layout";
import "./DependencyGraphModal.css";

interface Props {
  data: DependencyGraph | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}

const nodeTypes = { activity: ActivityNode };

const DependencyGraphModal: React.FC<Props> = ({ data, loading, error, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const { nodes, edges } = useMemo(() => {
    if (!data) return { nodes: [] as Node<ActivityNodeData>[], edges: [] as Edge[] };

    const rawNodes: Node<ActivityNodeData>[] = data.nodes.map((n) => ({
      id: String(n.activity_id),
      type: "activity",
      data: n as ActivityNodeData,
      position: { x: 0, y: 0 },
      draggable: true,
      selectable: true,
    }));

    const rawEdges: Edge[] = data.edges.map((e, i) => ({
      id: `e${i}-${e.from_activity_id}-${e.to_activity_id}`,
      source: String(e.from_activity_id),
      target: String(e.to_activity_id),
      type: "smoothstep",
      animated: false,
      label: `${predTypeLabel(e.pred_type)}${formatLagSuffix(e.lag_hours)}`,
      labelStyle: { fill: "var(--text, #e0e0e0)", fontSize: 10, fontWeight: 600 },
      labelBgStyle: { fill: "var(--bg-card, #222)", fillOpacity: 0.9, stroke: "var(--border, #333)" },
      labelBgPadding: [4, 2] as [number, number],
      labelBgBorderRadius: 3,
      style: e.crosses_boundary
        ? { stroke: "var(--accent, #5EA348)", strokeDasharray: "6 4", strokeWidth: 2 }
        : { stroke: "var(--text-muted, #8892a4)", strokeWidth: 1.5 },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: e.crosses_boundary ? "var(--accent, #5EA348)" : "var(--text-muted, #8892a4)",
      },
    }));

    return { nodes: layoutGraph(rawNodes, rawEdges), edges: rawEdges };
  }, [data]);

  return (
    <div
      className="depgraph-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="depgraph-modal" onClick={(e) => e.stopPropagation()}>
        <div className="depgraph-header">
          <div className="depgraph-title">
            {data?.title || "Dependency Graph"}
            {data && (
              <span className="depgraph-counts">
                {data.nodes.filter((n) => !n.external).length} in section,{" "}
                {data.nodes.filter((n) => n.external).length} external
              </span>
            )}
          </div>
          <button className="depgraph-close" onClick={onClose} title="Close (Esc)">
            ×
          </button>
        </div>
        <div className="depgraph-body">
          {loading && <div className="depgraph-status">Loading…</div>}
          {error && !loading && <div className="depgraph-status depgraph-error">{error}</div>}
          {data && !loading && !error && data.nodes.length === 0 && (
            <div className="depgraph-status">No activities in this group.</div>
          )}
          {data && !loading && !error && data.nodes.length > 0 && (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              fitView
              minZoom={0.2}
              maxZoom={2}
              proOptions={{ hideAttribution: true }}
            >
              <Background gap={20} size={1} />
              <Controls showInteractive={false} />
              <MiniMap pannable zoomable nodeColor={(n) => (n.data as ActivityNodeData).fill} />
            </ReactFlow>
          )}
        </div>
      </div>
    </div>
  );
};

export default DependencyGraphModal;
